import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import EvalItemTable from "./EvalItemTable";
import RubricDrawer from "./RubricDrawer";
import EvalPlanDrawer from "./EvalPlanDrawer";
import { useTranslation } from "react-i18next";
import useLessonStudents from "../../../../hooks/useLessonStudentData";

export default function SettingEvalItemsModal({
  lessonInfo,
  setLessonInfo,
  setEvalItemModalOpen,
  lessonSetting,
  evalItemsData,
  setEvalItemsData,
  getEvalItemsByLessonId,
}) {
  const location = useLocation();
  const paths = location.pathname.split("/");
  // 배열의 두 번째 요소를 schoolId로 사용합니다.
  const schoolId = paths[1];
  //아래 조건으로 데이터를 불러오도록 합니다.
  const { t } = useTranslation();

  // 이 과목에 이미 저장된 학생 점수가 있는지 확인 (가중치/만점/단계 변경 시 안내 및 하위단계 생성 차단용)
  const { getLessonStudentsByLessonId } = useLessonStudents();
  const getLessonStudentsByLessonIdRef = useRef(getLessonStudentsByLessonId);
  // 점수가 하나라도 기록된 평가항목 id 집합 (문자열)
  const [gradedItemIds, setGradedItemIds] = useState(() => new Set());
  const hasSavedGrades = gradedItemIds.size > 0;

  useEffect(() => {
    if (!schoolId || !lessonInfo?.id) return;

    getLessonStudentsByLessonIdRef.current.mutate(
      { schoolid: schoolId, lesson_id: lessonInfo.id },
      {
        onSuccess: (data) => {
          const ids = new Set();
          (data || []).forEach((student) => {
            const grades = student?.eval_item_grades;
            if (grades && typeof grades === "object") {
              Object.entries(grades).forEach(([itemId, value]) => {
                if (value !== "" && value !== null && value !== undefined) {
                  ids.add(String(itemId));
                }
              });
            }
          });
          setGradedItemIds(ids);
        },
        onError: (error) => {
          console.error("점수 저장 여부 확인 실패:", error);
        },
      }
    );
  }, [schoolId, lessonInfo?.id]);

  // 루브릭 Drawer 상태 관리
  const [rubricDrawerOpen, setRubricDrawerOpen] = useState(false);
  const [selectedItemForRubric, setSelectedItemForRubric] = useState(null);

  // 평가계획서 Drawer 상태 관리
  const [evalPlanDrawerOpen, setEvalPlanDrawerOpen] = useState(false);

  const handleClose = () => {
    setEvalItemModalOpen(false);
    setLessonInfo({
      id: "",
      evaluation_type: "",
      hierarchyOne: "",
      hierarchyTwo: "",
      hierarchyThree: "",
      school_year: "",
      schoolid: "",
      subject_name: "",
      teachers: [],
      termName: "",
      lesson_hours: "",
      extra_attributes: "",
    });
  };

  const handleOpenRubricDrawer = (item) => {
    setSelectedItemForRubric(item);
    setRubricDrawerOpen(true);
  };

  const handleCloseRubricDrawer = () => {
    setRubricDrawerOpen(false);
    setSelectedItemForRubric(null);
  };

  const handleOpenEvalPlanDrawer = () => {
    setEvalPlanDrawerOpen(true);
  };

  const handleCloseEvalPlanDrawer = () => {
    setEvalPlanDrawerOpen(false);
  };

  return (
    <>
      <div className="modal-container">
        <div
          className="modalForUnit"
          style={{
            width: "1200px",
            overflowY: "auto",
            overflowX: "auto",
          }}
        >
          <div className="modal-header mb-3 d-flex justify-content-between">
            <h3>{t("teacher.lessonRelated.evalItemSetting")}</h3>
            <div>
              <button
                type="button"
                className="btn btn-primary me-2"
                onClick={handleOpenEvalPlanDrawer}
              >
                {t("teacher.lessonRelated.evalPlan") || "평가계획서"}
              </button>
              <button
                type="button"
                className="btn btn-secondary me-3"
                onClick={handleClose}
              >
                {t("common.labels.close") || "닫기"}
              </button>
            </div>
          </div>
          <div className="modal-content">
            <table className="table text-center align-middle">
              <tbody>
                <tr>
                  <th style={{ width: "15%" }}>
                    {t("common.labels.termName")}
                  </th>
                  <th style={{ width: "10%" }}>
                    {t("common.labels.schoolStage")}
                  </th>
                  <th style={{ width: "10%" }}>
                    {t("common.labels.studentYear")}
                  </th>
                  <th style={{ width: "10%" }}>{t("common.labels.class")}</th>
                  <th style={{ width: "40%" }}>
                    {t("teacher.lessonRelated.subjectTitle")}
                  </th>
                  <th style={{ width: "15%" }}>{t("common.user.teacher")}</th>
                </tr>
                <tr>
                  <td>
                    {lessonInfo &&
                      lessonInfo.school_year + "-" + lessonInfo.termName}
                  </td>
                  <td>{lessonInfo && lessonInfo.hierarchyOne}</td>
                  <td>{lessonInfo && lessonInfo.hierarchyTwo}</td>
                  <td>{lessonInfo && lessonInfo.hierarchyThree}</td>
                  <td>{lessonInfo && lessonInfo.subject_name}</td>
                  <td>
                    {lessonInfo && Array.isArray(lessonInfo?.teachers)
                      ? lessonInfo?.teachers
                          ?.map((user) => user.tname.toString())
                          .join(",")
                      : ""}
                  </td>
                </tr>
              </tbody>
            </table>

            {hasSavedGrades && (
              <div
                className="alert alert-warning py-2 px-3"
                style={{ fontSize: "14px" }}
              >
                이 과목에는 이미 학생 점수가 저장되어 있습니다. 가중치·만점·평가제
                등을 수정하거나 항목을 추가/삭제하면, 이미 저장된 총점(성적)에는
                자동으로 반영되지 않습니다. 변경 후에는{" "}
                <strong>성적기록 화면에서 [모두 저장]</strong>을 눌러 재계산된
                값으로 다시 저장해야 반영됩니다.
              </div>
            )}

            <EvalItemTable
              conditions={{ schoolid: schoolId, lesson_id: lessonInfo.id }}
              lessonSetting={lessonSetting}
              evalItemsData={evalItemsData}
              setEvalItemsData={setEvalItemsData}
              getEvalItemsByLessonId={getEvalItemsByLessonId}
              onOpenRubricDrawer={handleOpenRubricDrawer}
              gradedItemIds={gradedItemIds}
            />
          </div>
        </div>
      </div>

      {/* 루브릭 Drawer */}
      {rubricDrawerOpen && selectedItemForRubric && (
        <RubricDrawer
          item={selectedItemForRubric}
          onClose={handleCloseRubricDrawer}
          getEvalItemsByLessonId={getEvalItemsByLessonId}
          setEvalItemsData={setEvalItemsData}
        />
      )}

      {/* 평가계획서 Drawer */}
      {evalPlanDrawerOpen && (
        <EvalPlanDrawer
          lessonInfo={lessonInfo}
          evalItemsData={evalItemsData}
          onClose={handleCloseEvalPlanDrawer}
          getEvalItemsByLessonId={getEvalItemsByLessonId}
          setEvalItemsData={setEvalItemsData}
          schoolId={schoolId}
        />
      )}
    </>
  );
}
